import { useState } from "react";
import { useLocation } from "react-router-dom";
import { LocaleLink as Link } from "@/components/landing/LocaleLink";
import { useTranslation } from "react-i18next";
import { Cookie, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useCookieConsent } from "@/contexts/CookieConsentContext";
import { isRecoveryAccessToken } from "@/lib/recovery-session";
import { isAppPath } from "@/lib/app-paths";
import { safeLocalStorage } from "@/lib/safe-storage";

// Banner de consentimiento de cookies. Reglas (founder + RGPD):
// - Tres opciones simétricas: Aceptar todo / Rechazar todo / Personalizar.
//   Ninguna se prioriza visualmente con tamaño o color desproporcionado —
//   se evita el dark pattern de "rechazar" oculto en menú secundario.
// - Las técnicas son siempre activas y el switch correspondiente está
//   deshabilitado y marcado con la etiqueta "Siempre activas".
// - Hasta que el usuario decide, los scripts no esenciales (Vercel
//   Analytics, etc.) NO se montan: ver lib/analytics-loader.tsx en App.tsx.

// Detecta si la pantalla actual es parte del flujo de reset de contraseña.
// Lo necesitamos para no mostrar el banner ahí: si el padre toca "ver
// política" en el banner y vuelve atrás, la sesión de recovery persistida
// puede provocar que entre logueado a la app saltándose el cambio de
// contraseña. Mientras esté en ese flow, evitamos exponer salidas.
function isInPasswordRecoveryFlow(pathname: string, search: string, hash: string): boolean {
  if (pathname.endsWith("/reset-password")) return true;
  if (pathname.startsWith("/app/")) {
    if (new URLSearchParams(search).get("reset") === "1") return true;
    if (hash.includes("type=recovery")) return true;
    if (typeof window !== "undefined" && window.__parentRecoveryTokens) return true;
    try {
      const raw = typeof window !== "undefined" ? safeLocalStorage.getItem("sb-parent-auth-token") : null;
      if (raw) {
        const parsed = JSON.parse(raw) as {
          access_token?: string;
          currentSession?: { access_token?: string };
          session?: { access_token?: string };
        };
        const token =
          parsed?.access_token ??
          parsed?.currentSession?.access_token ??
          parsed?.session?.access_token;
        if (isRecoveryAccessToken(token)) return true;
      }
    } catch { /* ignore — fail open */ }
  }
  return false;
}

export default function CookieBanner() {
  const { t } = useTranslation("cookieBanner");
  const { pathname, search, hash } = useLocation();
  const { needsDecision, customizing, openCustomizer, closeCustomizer, acceptAll, rejectAll, saveCustom } =
    useCookieConsent();

  const [analyticsOn, setAnalyticsOn] = useState(false);
  const [marketingOn, setMarketingOn] = useState(false);

  // Nunca durante el flujo de reset password: el link "ver política" abre una
  // puerta para saltarse el cambio de contraseña con el back del navegador.
  if (isInPasswordRecoveryFlow(pathname, search, hash)) return null;

  // El banner se monta cuando hay que decidir (primera visita) o cuando el
  // usuario abre el customizer manualmente desde el footer.
  if (!needsDecision && !customizing) return null;

  const isCustomizing = customizing || needsDecision === false;

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-banner-title"
      // LDT-717 · En la landing, con su tipografía de marca: el banner vive en
      // App, fuera de `.playium-landing`, y era lo único de la ruta pública
      // que pintaba en Inter y Space Grotesk — la landing descargaba las dos
      // solo por él. En el panel y la app de familias se ve como siempre.
      className={`${isAppPath(pathname) ? "" : "playium-brand-fonts "}fixed inset-x-0 bottom-0 z-[60] p-3 sm:p-4 pointer-events-none`}
    >
      <div className="max-w-4xl mx-auto bg-white rounded-2xl border border-gray-200 shadow-xl pointer-events-auto overflow-hidden">
        <div className="p-5 sm:p-6">
          <div className="flex items-start gap-3 mb-3">
            <div className="h-9 w-9 rounded-xl bg-playium-mint/10 flex items-center justify-center shrink-0">
              <Cookie className="h-5 w-5 text-playium-mint" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 id="cookie-banner-title" className="text-base font-semibold text-gray-900">
                {t("title")}
              </h2>
              <p className="text-sm text-gray-600 leading-relaxed mt-1">
                {t("body")}{" "}
                <Link
                  to="/cookies"
                  className="underline underline-offset-2 hover:text-gray-900"
                >
                  {t("moreInfo")}
                </Link>
              </p>
            </div>
            {customizing && !needsDecision && (
              <button
                onClick={closeCustomizer}
                aria-label="Close"
                className="text-gray-400 hover:text-gray-600 p-1 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-playium-mint/40"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Detalle por categoría: visible siempre que el usuario abra el
              customizer (también en primera visita si quiere granularidad). */}
          {isCustomizing && (
            <div className="mt-4 space-y-3 border-t border-gray-100 pt-4">
              <CategoryRow
                title={t("categories.technical.name")}
                desc={t("categories.technical.desc")}
                checked
                disabled
                badgeLabel={t("categories.technical.alwaysOn")}
                id="cookie-technical"
              />
              <CategoryRow
                title={t("categories.analytics.name")}
                desc={t("categories.analytics.desc")}
                checked={analyticsOn}
                onChange={setAnalyticsOn}
                id="cookie-analytics"
              />
              <CategoryRow
                title={t("categories.marketing.name")}
                desc={t("categories.marketing.desc")}
                checked={marketingOn}
                onChange={setMarketingOn}
                id="cookie-marketing"
              />
            </div>
          )}

          {/* Acciones — tres opciones del MISMO tamaño y peso visual.
              "Aceptar todo" no es más prominente que "Rechazar todo": ambas
              son `Button` size="lg" rounded-full, distinto color pero misma
              jerarquía. */}
          <div className="mt-5 flex flex-col sm:flex-row gap-2">
            <Button
              size="lg"
              variant="outline"
              className="rounded-full border-gray-200 sm:flex-1"
              onClick={rejectAll}
            >
              {t("rejectAll")}
            </Button>
            {!isCustomizing ? (
              <Button
                size="lg"
                variant="outline"
                className="rounded-full border-gray-200 sm:flex-1"
                onClick={openCustomizer}
              >
                {t("customize")}
              </Button>
            ) : (
              <Button
                size="lg"
                variant="outline"
                className="rounded-full border-gray-200 sm:flex-1"
                onClick={() => saveCustom({ analytics: analyticsOn, marketing: marketingOn })}
              >
                {t("save")}
              </Button>
            )}
            <Button
              size="lg"
              className="rounded-full bg-playium-mint hover:bg-playium-mint-soft text-white sm:flex-1"
              onClick={acceptAll}
            >
              {t("acceptAll")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

interface CategoryRowProps {
  id: string;
  title: string;
  desc: string;
  checked: boolean;
  disabled?: boolean;
  badgeLabel?: string;
  onChange?: (checked: boolean) => void;
}

function CategoryRow({ id, title, desc, checked, disabled, badgeLabel, onChange }: CategoryRowProps) {
  return (
    <div className="flex items-start gap-3">
      <Switch
        id={id}
        checked={checked}
        disabled={disabled}
        onCheckedChange={(v) => onChange?.(!!v)}
        className="mt-1 shrink-0"
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <Label htmlFor={id} className="text-sm font-semibold text-gray-900 cursor-pointer">
            {title}
          </Label>
          {badgeLabel && (
            <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 uppercase tracking-wide">
              {badgeLabel}
            </span>
          )}
        </div>
        <p className="text-xs text-gray-600 leading-relaxed mt-0.5">{desc}</p>
      </div>
    </div>
  );
}
